import { ConversationBubble, CursorStyle, Direction, Displaying, Spacing } from "@bonusx-global/bx-design-system";
import { SparklesSolid } from "@bonusx-global/bx-icons";
import styled from "@emotion/styled";
import Box from "@mui/material/Box";
import isEmpty from "lodash/isEmpty";
import { useCallback, useContext } from "react";
import useIsMobile from "../../../hooks/device/useIsMobile";
import { type IWithIsMobile } from "../../interfaces/IWithIsMobile.interface";
import type { AiChatbotCmsPrompt } from "../../../features/ai-chatbot/types/ai-chatbot-cms-prompt.entity";
import type { FetchStrategies } from "../../../features/ai-chatbot/constants/fetch-strategies.enum";
import { AiChatbotPromptManager } from "../../services/ai-chatbot/ai-prompt-manager/ai-prompt-manager";
import { ChatbotContext } from "../Chatbot/chatbot.context";
import { useChatbotMediator } from "../Chatbot/hooks/use-chatbot-manager/use-chatbot-mediator";
import { useChatbotPanel } from "../Chatbot/hooks/use-chatbot-panel/use-chatbot-panel";
import { useIsChatbotAvailableToUser } from "../Chatbot/hooks/use-is-chatbot-available-to-user/use-is-chatbot-available-to-user";
import { EmptyComponent } from "../common/EmptyComponent";
import AiChatbotLastChatButton from "./ai-chatbot-last-chat-button";
import { trackSuggestedPromptEvent } from "./utils/track-suggested-prompt-event";

interface AiChatbotSuggestedPromptProps {
  flexDirection: Direction;
  promptStrategy: FetchStrategies;
  hasLastChatButton: boolean;
  onClickLastChatButton?: () => void;
  suggestedPrompts: AiChatbotCmsPrompt[];
  eventNameToFireOnClick?: string;
}

function AiChatbotSuggestedPrompt({
  flexDirection,
  promptStrategy,
  hasLastChatButton,
  onClickLastChatButton,
  suggestedPrompts,
  eventNameToFireOnClick,
}: Readonly<AiChatbotSuggestedPromptProps>): JSX.Element {
  const isMobile = useIsMobile();
  const { openChatbotPanel } = useChatbotPanel();
  const { state } = useContext(ChatbotContext);
  const chatbotMediator = useChatbotMediator();
  const isChatbotAvailableForCurrentUser = useIsChatbotAvailableToUser();

  const onClickPromptCallback = useCallback(
    (prompt: AiChatbotCmsPrompt) => {
      openChatbotPanel();

      if (!isChatbotAvailableForCurrentUser) {
        return;
      }

      const promptManager = new AiChatbotPromptManager();
      const promptMessage = promptManager.getPrompt(prompt);
      const fallbackResponse = promptManager.getFallbackResponse(prompt);

      chatbotMediator.sendToAIService({ message: promptMessage, fallbackResponse });

      if (eventNameToFireOnClick) {
        trackSuggestedPromptEvent(eventNameToFireOnClick, promptMessage);
      }
    },
    [promptStrategy, isChatbotAvailableForCurrentUser, eventNameToFireOnClick, openChatbotPanel, chatbotMediator]
  );

  const isChatbotComputing = state.isSending || state.isStreamingResponse;

  if (isEmpty(suggestedPrompts) && !hasLastChatButton) {
    return <EmptyComponent />;
  }

  return (
    <AiChatbotSuggestedPromptContainer>
      <ScrollableContainer direction={flexDirection} isMobile={isMobile}>
        {hasLastChatButton && <AiChatbotLastChatButton onClick={onClickLastChatButton} />}
        {suggestedPrompts.map(function mapSuggestedPrompt(prompt) {
          return (
            <ConversationBubble
              key={`${prompt.priority}-${prompt.aiPromptInput.displayText}`}
              text={prompt.aiPromptInput.displayText}
              onClick={isChatbotComputing ? undefined : () => onClickPromptCallback(prompt)}
              startIcon={<SparklesSolid />}
              sx={isChatbotComputing ? { "&:hover": { cursor: CursorStyle.notAllowed } } : undefined}
            />
          );
        })}
      </ScrollableContainer>
    </AiChatbotSuggestedPromptContainer>
  );
}

export default AiChatbotSuggestedPrompt;

const nonForwardedProps = ["direction", "isMobile"];

function shouldForwardProp(prop: string) {
  return !nonForwardedProps.includes(prop);
}

const ScrollableContainer = styled(Box, { shouldForwardProp })<{ direction: Direction } & IWithIsMobile>(
  ({ direction, isMobile }) => ({
    display: Displaying.flex,
    flexWrap: isMobile ? "nowrap" : "wrap",
    overflowX: "auto",
    flexDirection: direction,
    gap: Spacing["500"],
    "& > .MuiBox-root": {
      flex: "0 0 auto",
    },
    "&::-webkit-scrollbar": {
      display: Displaying.none,
    },
  })
);

const AiChatbotSuggestedPromptContainer = styled(Box)({
  display: Displaying.flex,
  flexDirection: Direction.column,
  gap: Spacing["1000"],
});
